import React, { useState, useEffect, useRef } from "react";
import { api } from "@/context/AuthContext";
import { Library, Plus, Trash2, Pencil, RefreshCw, Upload, CheckCircle, AlertCircle, X, Sparkles } from "lucide-react";

const LINES = [
  { value: "all",                 label: "All Lines" },
  { value: "commercial_property", label: "Commercial Property" },
  { value: "general_liability",   label: "General Liability" },
  { value: "workers_comp",        label: "Workers' Comp" },
  { value: "habitational",        label: "Habitational" },
  { value: "commercial_auto",     label: "Commercial Auto" },
];
const LINE_LABEL = Object.fromEntries(LINES.map(l => [l.value, l.label]));
const EMPTY = { title: "", text: "", category: "", inspection_type: "all" };

function EntryEditor({ initial, onSave, onCancel, saving }) {
  const [e, setE] = useState(initial);
  return (
    <div className="border border-navy/30 bg-navy/[0.02] rounded-xl p-4 space-y-3" data-testid="rec-entry-editor">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input data-testid="rec-title-input" value={e.title} onChange={ev => setE({ ...e, title: ev.target.value })}
          placeholder="Title (e.g., Fire Extinguisher Placement)"
          className="h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
        <input data-testid="rec-category-input" value={e.category} onChange={ev => setE({ ...e, category: ev.target.value })}
          placeholder="Category (e.g., Fire Protection)"
          className="h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
        <select data-testid="rec-line-select" value={e.inspection_type} onChange={ev => setE({ ...e, inspection_type: ev.target.value })}
          className="h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white">
          {LINES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>
      <textarea data-testid="rec-text-input" value={e.text} onChange={ev => setE({ ...e, text: ev.target.value })} rows={3}
        placeholder="Standard recommendation wording the AI should use as its baseline…"
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-y" />
      <div className="flex items-center gap-2">
        <button type="button" data-testid="save-rec-entry-btn" disabled={saving || !e.title.trim() || !e.text.trim()}
          onClick={() => onSave(e)}
          className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

export default function RecLibraryTab() {
  const fileRef = useRef(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [draftSource, setDraftSource] = useState("");
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");

  const load = () => api.get("/carrier/rec-library").then(r => setEntries(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    setSaving(true); setError("");
    try {
      if (e.id) await api.put(`/carrier/rec-library/${e.id}`, e);
      else await api.post("/carrier/rec-library", e);
      setEditing(null);
      load();
    } catch (err) { setError(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await api.delete(`/carrier/rec-library/${id}`);
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true); setError(""); setDrafts([]);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/carrier/rec-library/import", fd, { timeout: 120000 });
      setDrafts(res.data.drafts || []);
      setDraftSource(res.data.source);
    } catch (err) { setError(err.response?.data?.detail || "Import failed"); }
    setImporting(false);
  };

  const saveAllDrafts = async () => {
    setSaving(true); setError("");
    try {
      const res = await api.post("/carrier/rec-library/bulk", { entries: drafts });
      setDrafts([]);
      load();
      setError("");
      setDraftSource(`saved:${res.data.saved}`);
    } catch (err) { setError(err.response?.data?.detail || "Failed to save entries"); }
    setSaving(false);
  };

  if (loading) return <div className="py-10 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>;

  const visible = filter ? entries.filter(e => e.inspection_type === filter || e.inspection_type === "all") : entries;

  return (
    <div className="space-y-5" data-testid="rec-library-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-800">Recommendation Library</h2>
          <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
            Your standard recommendations. The AI uses these as its <strong>baseline wording</strong> when writing findings —
            adapting them for context — and only writes custom recommendations when nothing in the library applies.
            Findings using your standards show a <span className="text-teal-600 font-medium">Carrier Standard</span> badge.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,.pdf,.docx,.txt,.md" className="hidden" data-testid="rec-import-input"
            onChange={e => { handleImport(e.target.files?.[0]); e.target.value = ""; }} />
          <button data-testid="import-rec-library-btn" onClick={() => fileRef.current?.click()} disabled={importing}
            className="flex items-center gap-2 px-3 py-2 border border-navy/30 text-navy rounded-lg text-sm font-medium hover:bg-navy/5 transition-colors disabled:opacity-50">
            <Upload className="w-4 h-4" /> {importing ? "Reading document…" : "Import CSV / PDF / Word"}
          </button>
          <button data-testid="new-rec-entry-btn" onClick={() => setEditing("new")}
            className="flex items-center gap-2 px-3 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">
            <Plus className="w-4 h-4" /> Add Recommendation
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="rec-library-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {editing === "new" && <EntryEditor initial={EMPTY} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />}

      {drafts.length > 0 && (
        <div className="border border-dashed border-navy/30 bg-navy/[0.02] rounded-xl p-4 space-y-3" data-testid="rec-drafts">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-navy" /> {drafts.length} recommendation{drafts.length !== 1 ? "s" : ""} found ({draftSource === "csv" ? "from CSV" : "AI-extracted"}) — review before saving
            </p>
            <div className="flex items-center gap-2">
              <button data-testid="save-all-drafts-btn" onClick={saveAllDrafts} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-navy text-white rounded-lg text-xs font-medium hover:bg-navy-dark disabled:opacity-50">
                <CheckCircle className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save All"}
              </button>
              <button onClick={() => setDrafts([])} className="text-xs text-slate-400 hover:text-slate-600">Discard</button>
            </div>
          </div>
          <ul className="space-y-2 max-h-72 overflow-y-auto">
            {drafts.map((d, i) => (
              <li key={i} className="flex items-start justify-between gap-3 bg-white border border-slate-100 rounded-lg p-3" data-testid={`rec-draft-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-700">{d.title}
                    <span className="ml-2 text-[11px] px-1.5 py-0.5 bg-slate-100 rounded-full text-slate-500">{LINE_LABEL[d.inspection_type] || "All Lines"}</span>
                    {d.category && <span className="ml-1 text-[11px] text-slate-400">{d.category}</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{d.text}</p>
                </div>
                <button onClick={() => setDrafts(prev => prev.filter((_, idx) => idx !== i))}
                  className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`remove-draft-${i}`}><X className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {entries.length > 0 && (
        <div className="flex items-center gap-2">
          <select data-testid="rec-filter-select" value={filter} onChange={e => setFilter(e.target.value)}
            className="h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-600">
            <option value="">All coverage lines</option>
            {LINES.filter(l => l.value !== "all").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
          <span className="text-xs text-slate-400">{visible.length} of {entries.length} entries</span>
        </div>
      )}

      {entries.length === 0 && drafts.length === 0 && editing !== "new" ? (
        <div className="py-10 text-center border border-dashed border-slate-200 rounded-xl" data-testid="no-rec-entries">
          <Library className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No standard recommendations yet</p>
          <p className="text-slate-400 text-sm mt-1">Import your recommendation library (CSV, PDF, or Word), or add entries manually. Until then, the AI writes custom recommendations.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(e => editing && editing.id === e.id ? (
            <EntryEditor key={e.id} initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />
          ) : (
            <div key={e.id} className="border border-slate-200 rounded-lg p-3.5 flex items-start justify-between gap-3" data-testid={`rec-entry-${e.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{e.title}
                  <span className="ml-2 text-[11px] px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-slate-500">{LINE_LABEL[e.inspection_type] || e.inspection_type}</span>
                  {e.category && <span className="ml-1.5 text-[11px] text-slate-400">{e.category}</span>}
                  {e.source !== "manual" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-navy/10 text-navy rounded-full font-semibold">IMPORTED</span>}
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{e.text}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button data-testid={`edit-rec-${e.id}`} onClick={() => setEditing(e)} className="p-1.5 text-slate-400 hover:text-navy"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-rec-${e.id}`} onClick={() => handleDelete(e.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
